import React, { useEffect, useContext, useRef, useState } from 'react';

import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';

import type { Comment, RefetchOptions } from './useAllCommentsData.ts';
import { useAllCommentsData } from './useAllCommentsData.ts';
import {
	categorizeComments,
	buildDefaultCommentsContentStatusFilters,
} from './commentDataManagerUtils.ts';
import { useProcessedCommentsDataActions } from './useProcessedCommentsData.ts';

// Initiates the loading of all comment data for the specified page in the background
// This component is also responsible for processing the raw comments data and updating
// the ProcessedCommentsDataStore with categorized results using default contentStatus filters.

// Experience tracking constants
const COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE = 'comments-data-background-load';

type CommentsDataBackgroundLoaderProps = {
	contentId: string;
};

export const CommentsDataBackgroundLoader = ({ contentId }: CommentsDataBackgroundLoaderProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();
	const {
		setProcessedData,
		setError,
		setRefetch,
		clearErrorAndResetIsDataProcessed,
		setDataToProcessed,
	} = useProcessedCommentsDataActions();

	// Track refetch count to force effect to run after every refetch
	const [refetchCount, setRefetchCount] = useState(0);
	// Track last processed data reference to detect when data is unchanged (cached)
	const lastProcessedDataRef = useRef<typeof data>(undefined);
	// Call the hook to load all comments - should only when content has settled and is ready so we
	// don't impact loading times
	// Always fetch with network-only to ensure we get fresh data
	const { loading, error, data, refetch } = useAllCommentsData({
		contentId,
		options: {
			skip: !contentId,
			fetchPolicy: 'network-only',
		},
	});

	// Set refetch function in store whenever it changes.
	// Wrap refetch so that when the view calls it (e.g. Retry), we clear error/processed state first.
	// Increment refetchCount in .then() to force the processing effect to run.
	// On refetch failure we always setError, then call the caller's onError if they passed one.
	useEffect(() => {
		if (refetch) {
			const wrappedRefetch = ({ onError: callerOnError }: RefetchOptions = {}) => {
				clearErrorAndResetIsDataProcessed();
				return refetch({
					onError: (err) => {
						setError(err instanceof Error ? err : new Error(String(err)));
						callerOnError?.(err);
					},
				}).then((result: any) => {
					setRefetchCount((c) => c + 1);
					return result;
				});
			};
			setRefetch(wrappedRefetch);
		}
	}, [refetch, setRefetch, clearErrorAndResetIsDataProcessed, setError]);

	// Process and update the ProcessedCommentsDataStore when data changes or refetch completes
	// This is the ONLY place where the processed data store should be updated
	// Depends on refetchCount to ensure effect runs after every refetch
	useEffect(() => {
		// Don't process data while loading
		if (loading) {
			return;
		}

		if (error) {
			setError(error);
			return;
		}

		// When data is undefined (e.g. refetch failed and error was set in the wrapper catch),
		// don't call setProcessedData or we'd overwrite that error with error: undefined.
		if (data === undefined) {
			return;
		}

		// Fast path: if data reference is same as last processed (cached data from refetch),
		// just flip isDataProcessed to true without reprocessing
		if (data === lastProcessedDataRef.current) {
			setDataToProcessed();
			return;
		}

		// Filter out null nodes and create array (empty array if no data)
		const allComments = (data?.comments?.nodes || []).filter(
			(node: Comment | null): node is Comment => node !== null,
		);

		// Categorize comments using default contentStatus filters
		const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);
		const categorized = categorizeComments(allComments, contentStatusFilters);

		// Update the processed data store with categorized results
		// Note: refetch is set separately via setRefetch, so we don't include it here
		setProcessedData({
			allComments,
			...categorized,
			isDataProcessed: true,
		});

		// Update ref to track this data as last processed
		lastProcessedDataRef.current = data;
	}, [
		data,
		loading,
		error,
		pageMode,
		setProcessedData,
		setError,
		contentId,
		refetchCount,
		setDataToProcessed,
	]);

	// Experience tracking: Handle success/failure when loading completes
	useEffect(() => {
		if (!contentId) {
			return;
		}

		// Only track completion if we're not still loading
		if (!loading) {
			if (error) {
				// Experience failed due to error
				experienceTracker.fail({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
					error,
				});
			} else {
				// Experience succeeded - comments data loaded successfully
				experienceTracker.succeed({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
				});
			}
		}
	}, [loading, error, contentId, experienceTracker]);

	// This component only triggers the hook, doesn't render anything other than the ExperienceStart,
	// which handles being rendered multiple times with the same props gracefully
	return (
		<ExperienceStart
			name={COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE}
			attributes={{
				...getCommentsDataManagerExperimentAttributes(),
				contentId,
			}}
		/>
	);
};
